'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { ProductsApi } from '@/lib/Apis';
import { Product } from '@/lib/Apis';
import { ProductCard } from '@/components/ProductCard';
import { Skeleton } from '@/components/ui/Skeleton';
import { Input } from '@/components/ui/Input';
import { Search, X, TrendingUp, Clock, ArrowRight } from 'lucide-react';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { useWishlist } from '@/providers/WishlistProvider';
import { useRouter } from 'next/navigation';
import { useBasePath } from '@/providers/BasePathProvider';
import { motion, AnimatePresence } from 'framer-motion';
import { RecommendedProductsSection } from '@/components/RecommendedProductsSection';

function levenshtein(a: string, b: string): number {
    const m = a.length, n = b.length;
    const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
        Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
    );
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            dp[i][j] = a[i - 1] === b[j - 1]
                ? dp[i - 1][j - 1]
                : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
        }
    }
    return dp[m][n];
}

function findClosestTerm(query: string, candidates: string[]): string | null {
    const q = query.toLowerCase();
    const qWords = q.split(/\s+/);
    let bestTerm = '';
    let bestScore = Infinity;

    for (const term of candidates) {
        const tLower = term.toLowerCase();
        const tWords = tLower.split(/\s+/);
        let termScore = Infinity;

        for (const qw of qWords) {
            if (qw.length < 3) continue;
            for (const tw of tWords) {
                const dist = levenshtein(qw, tw);
                const ratio = dist / Math.max(qw.length, tw.length);
                if (ratio < termScore) termScore = ratio;
            }
        }

        if (termScore < bestScore) {
            bestScore = termScore;
            bestTerm = term;
        }
    }

    // Only suggest if edit distance ratio is meaningful (< 0.45)
    return bestScore < 0.45 ? bestTerm : null;
}

export default function SearchPage() {
    const router = useRouter();
    const { buildPath } = useBasePath();
    const searchParams = useSearchParams();
    const searchQuery = searchParams.get('q') || '';
    const [products, setProducts] = useState<Product[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchInput, setSearchInput] = useState(searchQuery);
    const [hasSearched, setHasSearched] = useState(false);
    const { isInWishlist } = useWishlist();
    const [suggestions, setSuggestions] = useState<{ term: string; isFuzzy: boolean; product?: Product }[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [recentSearches, setRecentSearches] = useState<string[]>([]);
    const [isMobile, setIsMobile] = useState(false);
    const [didYouMean, setDidYouMean] = useState<string | null>(null);
    const [didYouMeanProduct, setDidYouMeanProduct] = useState<Product | null>(null);

    // Track screen size for responsive placeholder
    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 768px)');
        setIsMobile(mediaQuery.matches);
        
        const handleChange = (e: MediaQueryListEvent) => {
        setIsMobile(e.matches);
        };
        
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);
    // Load recent searches from localStorage
    useEffect(() => {
        const stored = localStorage.getItem('recent_searches');
        if (stored) {
            try {
                setRecentSearches(JSON.parse(stored));
            } catch (e) {
                console.error('Failed to parse recent searches', e);
            }
        }
    }, []);

    // Save search query to recent searches
    const saveRecentSearch = useCallback((query: string) => {
        if (!query.trim()) return;

        setRecentSearches((prev) => {
            const updated = [query, ...prev.filter(s => s !== query)].slice(0, 5);
            localStorage.setItem('recent_searches', JSON.stringify(updated));
            return updated;
        });
    }, []);

    // Cache of known product names/brands seen from API responses for fuzzy fallback
    const knownTermsRef = useRef<Set<string>>(new Set());
    const productsByNameRef = useRef<Map<string, Product>>(new Map());

    const cacheProductTerms = useCallback((products: Product[]) => {
        products.forEach((p) => {
            if (p.name) {
                knownTermsRef.current.add(p.name);
                productsByNameRef.current.set(p.name.toLowerCase(), p);
            }
            if (p.brand) knownTermsRef.current.add(p.brand);
        });
    }, []);

    // Seed the term cache on mount so fuzzy fallback works on first search
    useEffect(() => {
        const seed = async () => {
            try {
                const api = new ProductsApi(AXIOS_CONFIG);
                const res = await api.smartSearch('', 50);
                if (res.data?.products) cacheProductTerms(res.data.products);
            } catch { /* ignore */ }
        };
        seed();
    }, [cacheProductTerms]);

    // Live suggestions: debounced smartSearch as user types, with fuzzy ranking
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        const q = searchInput.trim();
        if (q.length < 2) {
            setSuggestions([]);
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                const api = new ProductsApi(AXIOS_CONFIG);
                const res = await api.smartSearch(q, 10);
                const rawProducts: Product[] = res.data?.products || [];

                // Feed results into the cache for future fuzzy lookups
                cacheProductTerms(rawProducts);

                const qLower = q.toLowerCase();
                const qWords = qLower.split(/\s+/).filter(w => w.length >= 2);
                const seen = new Set<string>();
                const scored: { term: string; score: number; isFuzzy: boolean; product?: Product }[] = [];

                // Map term (lowercase) → first product that owns it
                const termProductMap = new Map<string, Product>();
                rawProducts.forEach((product) => {
                    const name = product.name?.toLowerCase();
                    if (name && !termProductMap.has(name)) termProductMap.set(name, product);
                    const brand = product.brand?.toLowerCase();
                    if (brand && !termProductMap.has(brand)) termProductMap.set(brand, product);
                    (product.tags || []).forEach((t) => {
                        const tl = t.toLowerCase();
                        if (!termProductMap.has(tl)) termProductMap.set(tl, product);
                    });
                });

                rawProducts.forEach((product) => {
                    const candidates = [
                        product.name,
                        product.brand,
                        ...(product.tags || []),
                    ].filter(Boolean) as string[];

                    candidates.forEach((term) => {
                        if (seen.has(term)) return;
                        seen.add(term);

                        const tLower = term.toLowerCase();
                        const tWords = tLower.split(/\s+/);
                        const matchedProduct = termProductMap.get(tLower);

                        // Exact / prefix / substring = highest priority
                        if (tLower === qLower) { scored.push({ term, score: 100, isFuzzy: false, product: matchedProduct }); return; }
                        if (tLower.startsWith(qLower)) { scored.push({ term, score: 80, isFuzzy: false, product: matchedProduct }); return; }
                        if (tLower.includes(qLower)) { scored.push({ term, score: 60, isFuzzy: false, product: matchedProduct }); return; }

                        // Word-level fuzzy via levenshtein
                        let bestRatio = Infinity;
                        for (const qw of qWords) {
                            for (const tw of tWords) {
                                const ratio = levenshtein(qw, tw) / Math.max(qw.length, tw.length);
                                if (ratio < bestRatio) bestRatio = ratio;
                            }
                        }
                        // Include fuzzy matches with ratio < 0.45
                        if (bestRatio < 0.45) {
                            scored.push({ term, score: Math.round((1 - bestRatio) * 40), isFuzzy: true, product: matchedProduct });
                        }
                    });
                });

                // Fallback: if no exact/prefix/substring matches, check the cached terms
                const hasDirectMatch = scored.some(s => s.score >= 60);
                if (!hasDirectMatch && knownTermsRef.current.size > 0) {
                    const cachedCandidates = Array.from(knownTermsRef.current).filter(
                        (t) => !seen.has(t)
                    );
                    const closest = findClosestTerm(q, cachedCandidates);
                    if (closest) {
                        const cachedProduct = productsByNameRef.current.get(closest.toLowerCase());
                        scored.push({ term: closest, score: 50, isFuzzy: true, product: cachedProduct });
                    }
                }

                const top = scored
                    .sort((a, b) => b.score - a.score)
                    .slice(0, 5)
                    .map(({ term, isFuzzy, product }) => ({ term, isFuzzy, product }));

                setSuggestions(top);
            } catch {
                setSuggestions([]);
            }
        }, 300);

        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [searchInput, cacheProductTerms]);

    // Sanitize search input to handle special characters
    const sanitizeSearchInput = useCallback((input: string): string => {
        // Remove leading/trailing whitespace
        let sanitized = input.trim();

        // Replace multiple spaces with single space
        sanitized = sanitized.replace(/\s+/g, ' ');

        // Escape special regex characters for safe processing
        // but keep the original input for search API
        return sanitized;
    }, []);

    useEffect(() => {
        const fetchSearchResults = async () => {
            const sanitizedQuery = sanitizeSearchInput(searchQuery);

            if (!sanitizedQuery) {
                setProducts([]);
                setIsLoading(false);
                setHasSearched(false);
                return;
            }

            // Require at least 2 characters for search
            if (sanitizedQuery.length < 2) {
                setProducts([]);
                setIsLoading(false);
                setHasSearched(false);
                return;
            }

            try {
                setIsLoading(true);
                setDidYouMean(null);
                setDidYouMeanProduct(null);
                const api = new ProductsApi(AXIOS_CONFIG);

                // Use AI-powered smart search for typo tolerance and semantic matching
                const response = await api.smartSearch(sanitizedQuery, 20);

                if (response.data?.products) {
                    cacheProductTerms(response.data.products);
                    const query = sanitizedQuery.toLowerCase();
                    const queryWords = query.split(/\s+/).filter(w => w.length >= 3);

                    const scoreProduct = (product: Product): number => {
                        const name = product.name?.toLowerCase() || '';
                        const brand = product.brand?.toLowerCase() || '';
                        const desc = product.description?.toLowerCase() || '';
                        const tags = product.tags?.map(t => t.toLowerCase()) || [];
                        const allText = `${name} ${brand} ${desc} ${tags.join(' ')}`;

                        if (name === query) return 100;
                        if (name.startsWith(query)) return 80;
                        if (name.includes(query)) return 60;
                        if (brand === query) return 50;
                        if (brand.startsWith(query)) return 40;
                        if (brand.includes(query)) return 30;
                        if (tags.some(t => t === query)) return 20;
                        if (tags.some(t => t.includes(query))) return 15;
                        if (desc.includes(query)) return 10;
                        // Match individual meaningful words from the query
                        if (queryWords.length > 0 && queryWords.some(w => allText.includes(w))) return 5;
                        return 0;
                    };

                    const transformedProducts = response.data.products
                        .map((item: Product) => ({ ...item, id: item._id || '' }))
                        .filter((p: Product) => scoreProduct(p) > 0)
                        .sort((a: Product, b: Product) => scoreProduct(b) - scoreProduct(a));

                    setProducts(transformedProducts);

                    // Save to recent searches if results found
                    if (transformedProducts.length > 0) {
                        saveRecentSearch(sanitizedQuery);
                    } else if (response.data.products.length > 0) {
                        // No filtered results but API returned candidates — suggest a correction
                        const candidates = response.data.products.flatMap((p: Product) =>
                            [p.name, p.brand].filter(Boolean) as string[]
                        );
                        const closest = findClosestTerm(sanitizedQuery, candidates);
                        setDidYouMean(closest);
                        if (closest) {
                            const matchedProduct = response.data.products.find((p: Product) =>
                                p.name?.toLowerCase() === closest.toLowerCase() ||
                                p.brand?.toLowerCase() === closest.toLowerCase()
                            ) || null;
                            setDidYouMeanProduct(matchedProduct);
                        }
                    }
                } else {
                    setProducts([]);
                }
                setHasSearched(true);
            } catch (error) {
                console.error('Error fetching smart search results:', error);
                if (error instanceof Error) {
                    console.error('Error details:', error.message);
                }
                setProducts([]);
                setHasSearched(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSearchResults();
    }, [searchQuery, sanitizeSearchInput, saveRecentSearch]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const sanitized = sanitizeSearchInput(searchInput);
        if (sanitized) {
            setShowSuggestions(false);
            router.push(buildPath(`/search?q=${encodeURIComponent(sanitized)}`));
        }
    };

    const clearSearch = () => {
        setSearchInput('');
        setShowSuggestions(false);
    };

    const handleSuggestionClick = (suggestion: string) => {
        setSearchInput(suggestion);
        setShowSuggestions(false);
        router.push(buildPath(`/search?q=${encodeURIComponent(suggestion)}`));
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);
        setShowSuggestions(value.trim().length >= 2);
    };

    const handleInputFocus = () => {
        if (searchInput.trim().length >= 2) {
            setShowSuggestions(true);
        }
    };

    // Four full-width columns produce the same card width as the shop's
    // three-column product area beside its desktop filters sidebar.
    const productGridClassName = 'grid grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4';

    return (
        <div className="min-h-screen bg-white">
            {/* Header Section */}
            <div className="bg-hsecondary/[0.03] border-b border-hsecondary/5">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-12">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-bold text-hsecondary tracking-tight">
                                {searchQuery ? `Results for "${searchQuery}"` : 'Search Products'}
                            </h1>
                            {products.length > 0 && searchQuery && (
                                <p className="text-hmuted mt-2">
                                    Found {products.length} {products.length === 1 ? 'product' : 'products'}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="max-w-2xl">
                        <form onSubmit={handleSearch} className="relative">
                            <div className="relative group">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-hprimary transition-colors" />
                                <Input
                                    type="text"
                                    value={searchInput}
                                    onChange={handleInputChange}
                                    onFocus={handleInputFocus}
                                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                    placeholder={isMobile ? "Search..." : "Search for medicines, vitamins, brands..."}
                                    className="pl-12 pr-28 py-7 text-lg rounded-2xl border-gray-200 bg-white shadow-sm hover:shadow-md focus:ring-4 focus:ring-hprimary/10 focus:border-hprimary transition-all duration-300"
                                    autoComplete="off"
                                />
                                <AnimatePresence>
                                    {searchInput && (
                                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={clearSearch}
                                                className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                                            >
                                                <X className="h-5 w-5" />
                                            </button>
                                            <div className="w-px h-6 bg-gray-200" />
                                            <motion.button
                                                initial={{ opacity: 0, scale: 0.8, x: -10 }}
                                                animate={{ opacity: 1, scale: 1, x: 0 }}
                                                exit={{ opacity: 0, scale: 0.8, x: -10 }}
                                                type="submit"
                                                className="p-2 bg-hprimary text-white rounded-xl hover:bg-hprimary/90 transition-colors shadow-md flex items-center justify-center"
                                                title="Search"
                                            >
                                                <ArrowRight className="h-5 w-5" />
                                            </motion.button>
                                        </div>
                                    )}
                                </AnimatePresence>

                                {/* Search Suggestions Dropdown */}
                                <AnimatePresence>
                                    {showSuggestions && (suggestions.length > 0 || recentSearches.length > 0) && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10, scale: 0.98 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 10, scale: 0.98 }}
                                            transition={{ duration: 0.2 }}
                                            className="absolute z-50 w-full mt-3 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
                                        >
                                            {/* Real-time Suggestions */}
                                            {suggestions.length > 0 && (
                                                <div className="p-2">
                                                    <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                                        <TrendingUp className="h-3 w-3" />
                                                        Suggestions
                                                    </div>
                                                    {suggestions.map(({ term, isFuzzy, product }, index) => (
                                                        <div key={index}>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleSuggestionClick(term)}
                                                                className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-xl transition-colors flex items-center gap-3 group"
                                                            >
                                                                <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-hprimary/10 transition-colors">
                                                                    <Search className="h-4 w-4 text-gray-500 group-hover:text-hprimary" />
                                                                </div>
                                                                <span className="font-medium text-gray-700 group-hover:text-gray-900 flex items-center gap-2">
                                                                    {isFuzzy && (
                                                                        <span className="text-xs font-normal text-hprimary/70">Did you mean</span>
                                                                    )}
                                                                    <span className={isFuzzy ? 'text-hprimary font-semibold' : ''}>
                                                                        {term}
                                                                    </span>
                                                                    {isFuzzy && <span className="text-xs font-normal text-hprimary/70">?</span>}
                                                                </span>
                                                            </button>
                                                            {product && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => router.push(buildPath(`/products/${product._id || product.id}`))}
                                                                    className="w-full mx-2 mb-2 p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors flex items-center gap-3 group"
                                                                    style={{ width: 'calc(100% - 16px)' }}
                                                                >
                                                                    {product.media?.[0]?.file ? (
                                                                        <img
                                                                            src={product.media[0].file}
                                                                            alt={product.name}
                                                                            className="w-12 h-12 rounded-lg object-cover bg-white border border-gray-100 flex-shrink-0"
                                                                        />
                                                                    ) : (
                                                                        <div className="w-12 h-12 rounded-lg bg-white border border-gray-100 flex items-center justify-center flex-shrink-0">
                                                                            <Search className="h-5 w-5 text-gray-300" />
                                                                        </div>
                                                                    )}
                                                                    <div className="flex-1 min-w-0 text-left">
                                                                        <p className="text-sm font-medium text-gray-800 truncate group-hover:text-gray-900">
                                                                            {product.name}
                                                                        </p>
                                                                        <div className="flex items-center gap-2 mt-0.5">
                                                                            {product.variants?.[0]?.finalPrice != null && (
                                                                                <span className="text-sm font-semibold text-hprimary">
                                                                                    ${product.variants[0].finalPrice.toFixed(2)}
                                                                                </span>
                                                                            )}
                                                                            {product.variants?.[0]?.isDiscounted && product.variants[0].retailPrice != null && (
                                                                                <span className="text-xs text-gray-400 line-through">
                                                                                    ${product.variants[0].retailPrice.toFixed(2)}
                                                                                </span>
                                                                            )}
                                                                            {product.brand && (
                                                                                <span className="text-xs text-gray-400">
                                                                                    {product.brand}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-hprimary transition-colors flex-shrink-0" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Recent Searches */}
                                            {recentSearches.length > 0 && !searchInput.trim() && (
                                                <div className="p-2 border-t border-gray-100">
                                                    <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                                        <Clock className="h-3 w-3" />
                                                        Recent Searches
                                                    </div>
                                                    {recentSearches.map((search, index) => (
                                                        <button
                                                            key={index}
                                                            type="button"
                                                            onClick={() => handleSuggestionClick(search)}
                                                            className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-xl transition-colors flex items-center gap-3 group"
                                                        >
                                                            <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                                                                <Clock className="h-4 w-4 text-gray-500 group-hover:text-gray-700" />
                                                            </div>
                                                            <span className="font-medium text-gray-700 group-hover:text-gray-900">
                                                                {search}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12">
                {isLoading ? (
                    <div className={productGridClassName}>
                        {[...Array(10)].map((_, i) => (
                            <div key={i} className="space-y-3">
                                <Skeleton className="aspect-[4/5] w-full rounded-2xl" />
                                <Skeleton className="h-4 w-3/4" />
                                <Skeleton className="h-4 w-1/2" />
                            </div>
                        ))}
                    </div>
                ) : products.length > 0 ? (
                    <div className={productGridClassName}>
                        {products.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                isFavorited={isInWishlist(product.id)}
                                onClickProduct={(p) => router.push(buildPath(`/products/${p.id}`))}
                            />
                        ))}
                    </div>
                ) : hasSearched ? (
                    <div className="text-center py-20 bg-gray-50 rounded-[32px]">
                        <div className="mb-8">
                            <div className="w-20 h-20 bg-white shadow-sm rounded-full flex items-center justify-center mx-auto mb-6">
                                <Search className="h-8 w-8 text-gray-300" />
                            </div>
                            <h3 className="text-2xl font-bold text-hsecondary mb-3">
                                No products found
                            </h3>
                            <p className="text-hmuted text-lg mb-4 max-w-md mx-auto">
                                We couldn&apos;t find any matches for &quot;{searchQuery}&quot;. Try generic terms like "Pain relief" or "Vitamin C".
                            </p>
                            {didYouMean && (
                                <div className="mb-6">
                                    <p className="text-hmuted text-base mb-4">
                                        Did you mean{' '}
                                        <button
                                            onClick={() => {
                                                setSearchInput(didYouMean);
                                                router.push(buildPath(`/search?q=${encodeURIComponent(didYouMean)}`));
                                            }}
                                            className="text-hprimary font-semibold underline underline-offset-2 hover:text-hprimary/80 transition-colors"
                                        >
                                            {didYouMean}
                                        </button>
                                        ?
                                    </p>
                                    {didYouMeanProduct && (
                                        <button
                                            onClick={() => router.push(buildPath(`/products/${didYouMeanProduct._id || didYouMeanProduct.id}`))}
                                            className="mx-auto max-w-sm w-full p-4 bg-white hover:bg-gray-50 rounded-2xl border border-gray-200 hover:border-gray-300 transition-all shadow-sm hover:shadow-md flex items-center gap-4 group"
                                        >
                                            {didYouMeanProduct.media?.[0]?.file ? (
                                                <img
                                                    src={didYouMeanProduct.media[0].file}
                                                    alt={didYouMeanProduct.name}
                                                    className="w-16 h-16 rounded-xl object-cover bg-gray-50 border border-gray-100 flex-shrink-0"
                                                />
                                            ) : (
                                                <div className="w-16 h-16 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0">
                                                    <Search className="h-6 w-6 text-gray-300" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0 text-left">
                                                <p className="text-sm font-semibold text-gray-800 truncate group-hover:text-gray-900">
                                                    {didYouMeanProduct.name}
                                                </p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    {didYouMeanProduct.variants?.[0]?.finalPrice != null && (
                                                        <span className="text-sm font-bold text-hprimary">
                                                            ${didYouMeanProduct.variants[0].finalPrice.toFixed(2)}
                                                        </span>
                                                    )}
                                                    {didYouMeanProduct.variants?.[0]?.isDiscounted && didYouMeanProduct.variants[0].retailPrice != null && (
                                                        <span className="text-xs text-gray-400 line-through">
                                                            ${didYouMeanProduct.variants[0].retailPrice.toFixed(2)}
                                                        </span>
                                                    )}
                                                </div>
                                                {didYouMeanProduct.brand && (
                                                    <p className="text-xs text-gray-400 mt-0.5">{didYouMeanProduct.brand}</p>
                                                )}
                                            </div>
                                            <ArrowRight className="h-5 w-5 text-gray-300 group-hover:text-hprimary transition-colors flex-shrink-0" />
                                        </button>
                                    )}
                                </div>
                            )}
                            <button
                                onClick={clearSearch}
                                className="px-8 py-3 bg-white text-hsecondary font-semibold rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
                            >
                                Clear Search
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-24">
                        <div className="w-24 h-24 bg-hsecondary/[0.03] rounded-full flex items-center justify-center mx-auto mb-6">
                            <Search className="h-10 w-10 text-hprimary" />
                        </div>
                        <h3 className="text-2xl font-bold text-hsecondary mb-3">
                            Start Searching
                        </h3>
                        <p className="text-hmuted mb-8 text-lg">
                            Enter a search term above to find products
                        </p>
                    </div>
                )}
            </div>

            {/* Recommendations Section */}
            <RecommendedProductsSection
                title="You may also like"
                subtitle="Popular products selected for you"
                initialLimit={5}
                increment={5}
            />
        </div>
    );
}
